"use client"

import { useMemo } from "react"
import { create } from "zustand"
import { persist, createJSONStorage } from "zustand/middleware"
import { createDebouncedStorage } from "@/lib/debounced-storage"

export type OverlayMode = "off" | "visual" | "live-drawing" | "audio" | "motion"

/** A drawn/rotated coordinate box, in percent-of-video-container units (0-100). angle in degrees. */
export interface OverlayBox {
  x: number
  y: number
  w: number
  h: number
  angle: number
}

/** A time range (ms) during which `box` is the active coordinate space for the indicator dot. */
export interface OverlaySection {
  id: string
  startMs: number
  endMs: number
  box: OverlayBox
}

interface VisualSettings {
  enabled: boolean
  style: "dot" | "bar" | "line"
  opacity: number // 0-100
  color: string
  size: number
  x: number // position on video
  y: number
  // Legacy single global box, kept only so pre-sections installs still render in the same place —
  // no longer written to. See ScriptOverlayManager's legacy-section fallback.
  rangeLeft: number
  rangeRight: number
  rangeTop: number
  rangeBottom: number
  sections: OverlaySection[]
}

interface LiveDrawingSettings {
  graphWindow: number // seconds to show
  graphHeight: number // pixels
  showGrid: boolean
  gridInterval: number // seconds
  smoothing: number // 0-100
  recordingMode: "continuous" | "on-change"
  controlType: "y-slider" | "2d-pad" | "octograph"
}

interface AudioTrackingSettings {
  detectorMode: "full" | "targeted"
  enabledTargets: Array<"kick" | "bass" | "snareClap" | "custom">
  customEqMode: "pillar" | "fluid"
  customPillarBands: number[]
  followPlayhead: boolean
  monitorEqPlayback: boolean
  customLowHz: number
  customHighHz: number
  customEqSlope: number
  customEqHardCutoff: boolean
  fullSensitivity: number
  fullMinGapMs: number
  targetedSensitivity: number
  targetedMinGapMs: number
  peakFloor: number
  peakCeiling: number
  /** When true, a beat drives the toy DOWN (pos toward 0) instead of up (pos toward 100). */
  invert: boolean
  /** What happens on reaching the analysis range's out point during preview playback (not Record,
   *  which always stops-and-commits there):
   *  "stop" = pause exactly at the out point.
   *  "loop" = jump back to the in point and keep playing.
   *  "returnToStart" = jump back to the in point and pause. */
  rangeEndBehavior: "stop" | "loop" | "returnToStart"
  /** Optional tempo grid overlay: null = off. Layered alongside onset detection, not a replacement. */
  bpm: number | null
  /** Anchor time (ms) that the beat grid is phase-locked to; grid lines are bpm intervals from here. */
  bpmOffsetMs: number
  /** When true, Apply snaps each detected hit to the nearest grid line instead of its raw detected time. */
  snapToBpm: boolean
}

interface MotionTrackingSettings {
  trackerMode: "motion" | "cellShaded" | "hybrid" | "color" | "region"
  motionTarget: "center" | "stationary" | "left" | "right" | "top" | "bottom"
  trackStartMode: "zero" | "continue" | "playhead" | "range"
  motionThreshold: number
  minMotionPixels: number
  motionAreaAutoFit: boolean
  roiAngle: number
  smoothing: number
  timingLead: number
  keySpacingMs: number
  pointReduction: "peaks" | "balanced" | "detailed" | "raw"
  pointDetail: number
  fps: number
  patchSize: number
  searchRadius: number
  searchAreaMode: "circle" | "directional" | "roi"
  regionTolerance: number
  axis: "vertical" | "horizontal" | "combined" | "combinedNormalized" | "size"
  maskSizeAutoFit: boolean
  maskSizeFloor: number
  maskSizeCeiling: number
  outputFloor: number
  outputCeiling: number
  roiColor: string
  roiFillColor: string
  roiHandleColor: string
  roiRotateColor: string
  roiFloorColor: string
  roiCeilingColor: string
  roiTargetColor: string
  roiLabelBackground: string
  touchControls: boolean
}

// Shared between ScriptOverlayManager (the video overlay: dot, box, faint outlines of other
// sections) and ScriptOverlaySectionsFooter (the sections strip + redraw/frame controls, rendered
// in a separate part of the page via ResizableMediaContainer's footerControls slot so it gets real
// layout space instead of covering the video). They're two different mount points reading/writing
// the same UI state, hence living in the store rather than component-local useState.
interface SectionsUIState {
  showSections: boolean
  selectedSectionId: string | null
  isRedrawing: boolean
}

interface ScriptOverlaySettings {
  activeMode: OverlayMode
  visual: VisualSettings
  liveDrawing: LiveDrawingSettings
  audio: AudioTrackingSettings
  motion: MotionTrackingSettings
  sectionsUI: SectionsUIState
}

interface ScriptOverlayStore extends ScriptOverlaySettings {
  setActiveMode: (mode: OverlayMode) => void
  updateVisual: (updates: Partial<VisualSettings>) => void
  setVisualSections: (sections: OverlaySection[]) => void
  updateLiveDrawing: (updates: Partial<LiveDrawingSettings>) => void
  updateAudio: (updates: Partial<AudioTrackingSettings>) => void
  updateMotion: (updates: Partial<MotionTrackingSettings>) => void
  updateSectionsUI: (updates: Partial<SectionsUIState>) => void
  toggleVisual: () => void
}

/**
 * Combines what was saved with the current defaults. The overlay always starts OFF: whether it is showing is a
 * per-session choice, not a saved one (some people never want it, and a saved "on" brought it back on every
 * video). Its look (style, colour, size, position, sections) is still remembered.
 */
export function mergePersistedOverlaySettings<T extends ScriptOverlaySettings>(persisted: unknown, current: T): T {
  const saved = persisted as Partial<ScriptOverlaySettings> | undefined
  return {
    ...current,
    ...saved,
    activeMode: current.activeMode,
    visual: { ...current.visual, ...saved?.visual, enabled: current.visual.enabled, sections: saved?.visual?.sections ?? [] },
    liveDrawing: { ...current.liveDrawing, ...saved?.liveDrawing },
    audio: { ...current.audio, ...saved?.audio },
    motion: { ...current.motion, ...saved?.motion },
    // isRedrawing is a transient drag-gesture flag — never restore it as true from a stale save.
    sectionsUI: { ...current.sectionsUI, ...saved?.sectionsUI, isRedrawing: false },
  } as T
}

const defaultSettings: ScriptOverlaySettings = {
  activeMode: "off",
  visual: {
    enabled: false,
    style: "dot",
    opacity: 80,
    color: "#3b82f6",
    size: 24,
    x: 50,
    y: 50,
    rangeLeft: 20,
    rangeRight: 80,
    rangeTop: 20,
    rangeBottom: 80,
    sections: [],
  },
  liveDrawing: {
    graphWindow: 5,
    graphHeight: 128,
    showGrid: true,
    gridInterval: 1,
    smoothing: 30,
    recordingMode: "continuous",
    controlType: "y-slider",
  },
  audio: {
    detectorMode: "targeted",
    enabledTargets: ["kick"],
    customEqMode: "fluid",
    customPillarBands: [2, 3, 4],
    followPlayhead: true,
    monitorEqPlayback: false,
    customLowHz: 70,
    customHighHz: 130,
    customEqSlope: 2,
    customEqHardCutoff: false,
    fullSensitivity: 1.5,
    fullMinGapMs: 40,
    targetedSensitivity: 1.5,
    targetedMinGapMs: 40,
    peakFloor: 40,
    peakCeiling: 100,
    invert: false,
    rangeEndBehavior: "stop",
    bpm: null,
    bpmOffsetMs: 0,
    snapToBpm: false,
  },
  motion: {
    trackerMode: "hybrid",
    motionTarget: "center",
    trackStartMode: "playhead",
    motionThreshold: 22,
    minMotionPixels: 6,
    motionAreaAutoFit: true,
    roiAngle: 0,
    smoothing: 25,
    timingLead: 0,
    keySpacingMs: 40,
    pointReduction: "balanced",
    pointDetail: 55,
    fps: 30,
    patchSize: 24,
    searchRadius: 80,
    searchAreaMode: "directional",
    regionTolerance: 105,
    axis: "vertical",
    maskSizeAutoFit: true,
    maskSizeFloor: 0,
    maskSizeCeiling: 100,
    outputFloor: 0,
    outputCeiling: 100,
    roiColor: "#22c55e",
    roiFillColor: "#22c55e",
    roiHandleColor: "#22c55e",
    roiRotateColor: "#f59e0b",
    roiFloorColor: "#f59e0b",
    roiCeilingColor: "#22c55e",
    roiTargetColor: "#60a5fa",
    roiLabelBackground: "#0f172a",
    touchControls: false,
  },
  sectionsUI: {
    // Defaults open: a bare dot with no visible box gives no clue what it's for.
    showSections: true,
    selectedSectionId: null,
    isRedrawing: false,
  },
}

export const useScriptOverlayStore = create<ScriptOverlayStore>()(
  persist(
    (set) => ({
      ...defaultSettings,

      setActiveMode: (mode) => set({ activeMode: mode }),

      updateVisual: (updates) =>
        set((state) => ({
          visual: { ...state.visual, ...updates },
        })),

      setVisualSections: (sections) =>
        set((state) => ({
          visual: { ...state.visual, sections },
        })),

      updateLiveDrawing: (updates) =>
        set((state) => ({
          liveDrawing: { ...state.liveDrawing, ...updates },
        })),

      updateAudio: (updates) =>
        set((state) => ({
          audio: { ...state.audio, ...updates },
        })),

      updateMotion: (updates) =>
        set((state) => ({
          motion: { ...state.motion, ...updates },
        })),

      updateSectionsUI: (updates) =>
        set((state) => ({
          sectionsUI: { ...state.sectionsUI, ...updates },
        })),

      toggleVisual: () =>
        set((state) => ({
          visual: { ...state.visual, enabled: !state.visual.enabled },
        })),
    }),
    {
      name: "script-overlay-settings",
      merge: mergePersistedOverlaySettings,
      // Dragging the overlay dot (follow mode) calls updateVisual on every pointermove — debounce
      // the actual localStorage write so that doesn't block the drag (see debounced-storage.ts).
      storage: createJSONStorage(() => createDebouncedStorage()),
    }
  )
)

// Shared by ScriptOverlayManager (the overlay) and ScriptOverlaySectionsFooter (the strip, mounted
// separately via ResizableMediaContainer's footerControls) so both derive the same section list
// from the store instead of duplicating the legacy-fallback logic.
export function useOverlaySections(durationMs: number) {
  const visual = useScriptOverlayStore((s) => s.visual)
  return useMemo(() => {
    const legacy: OverlaySection = {
      id: "legacy",
      startMs: 0,
      endMs: Math.max(1, durationMs),
      box: {
        x: visual.rangeLeft,
        y: visual.rangeTop,
        w: visual.rangeRight - visual.rangeLeft,
        h: visual.rangeBottom - visual.rangeTop,
        angle: 0,
      },
    }
    const sections = visual.sections.length > 0 ? visual.sections : [legacy]
    return [...sections].sort((a, b) => a.startMs - b.startMs)
  }, [visual.sections, visual.rangeLeft, visual.rangeRight, visual.rangeTop, visual.rangeBottom, durationMs])
}

export function findActiveSection(sortedSections: OverlaySection[], currentTimeMs: number): OverlaySection {
  return (
    sortedSections.find((s) => currentTimeMs >= s.startMs && currentTimeMs < s.endMs) ??
    sortedSections[sortedSections.length - 1]
  )
}
